import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Shared";
import { useT, useLang, LANGUAGES } from "@/lib/i18n";
import { Upload, X, Plus, Check, MapPin, Trash2, Globe } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const t = useT();
  const { setLang } = useLang();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", avatar_url: user?.avatar_url || "", language: user?.language || "nl" });
  const [saving, setSaving] = useState(false);
  const [code, setCode] = useState("");
  const [preview, setPreview] = useState(null); // null | object | 'invalid'
  const [restaurants, setRestaurants] = useState([]);
  const fileRef = useRef(null);

  const isChef = user?.role === "chef";

  useEffect(() => {
    setForm({ name: user?.name || "", phone: user?.phone || "", avatar_url: user?.avatar_url || "", language: user?.language || "nl" });
  }, [user]);

  useEffect(() => {
    if (!isChef) return;
    api.get("/restaurants").then((r) => setRestaurants(r.data)).catch(() => {});
  }, [isChef]);

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Foto te groot (max 2 MB)"); return; }
    const reader = new FileReader();
    reader.onload = (e) => setForm((f) => ({ ...f, avatar_url: e.target.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/profile", form);
      await refreshUser();
      if (form.language) setLang(form.language);
      toast.success("Profiel opgeslagen");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Mislukt");
    } finally { setSaving(false); }
  };

  const lookup = async (v) => {
    setCode(v.toUpperCase());
    if (v.trim().length < 4) { setPreview(null); return; }
    try {
      const { data } = await api.get("/public/restaurants/lookup", { params: { code: v.trim().toUpperCase() } });
      setPreview(data);
    } catch { setPreview("invalid"); }
  };

  const addRestaurant = async () => {
    if (!preview || preview === "invalid") return;
    try {
      await api.post("/auth/restaurants/add-code", { invite_code: code.trim().toUpperCase() });
      await refreshUser();
      toast.success(`Toegevoegd: ${preview.name}`);
      setCode(""); setPreview(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Mislukt");
    }
  };

  const removeRestaurant = async (rid) => {
    if (!confirm("Restaurant ontkoppelen van je account?")) return;
    try {
      await api.delete(`/auth/restaurants/${rid}`);
      await refreshUser();
      toast.success("Ontkoppeld");
    } catch {
      toast.error("Mislukt");
    }
  };

  const myRestaurantIds = user?.restaurant_ids?.length ? user.restaurant_ids : (user?.restaurant_id ? [user.restaurant_id] : []);
  const myRestaurants = restaurants.filter((r) => myRestaurantIds.includes(r.id));

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[900px] mx-auto" data-testid="profile-page">
      <PageHeader overline="Account" title="Mijn profiel" description="Beheer je gegevens, foto en gekoppelde restaurants." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="card-flat p-6 flex flex-col items-center text-center" data-testid="avatar-card">
          <div className="w-28 h-28 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center overflow-hidden mb-4">
            {form.avatar_url ? (
              <img src={form.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-3xl font-bold text-[#1B2D5C]">{user?.name?.charAt(0)?.toUpperCase() || "?"}</span>
            )}
          </div>
          <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="avatar-upload" />
          <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 h-9 px-3 border border-zinc-300 hover:border-[#5BC044] text-sm rounded-md text-zinc-700">
            <Upload size={14} /> Foto kiezen
          </button>
          {form.avatar_url && (
            <button onClick={() => setForm({ ...form, avatar_url: "" })} className="text-xs text-zinc-500 hover:text-red-600 mt-2 inline-flex items-center gap-1" data-testid="avatar-remove">
              <X size={12} /> Foto verwijderen
            </button>
          )}
        </div>

        <div className="lg:col-span-2 card-flat p-6" data-testid="profile-fields-card">
          <div className="overline mb-4">Gegevens</div>
          <div className="space-y-3">
            <div>
              <label className="overline block mb-1.5">Naam</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044]"
                data-testid="profile-name"
              />
            </div>
            <div>
              <label className="overline block mb-1.5">E-mail</label>
              <input value={user?.email || ""} disabled
                className="w-full h-10 px-3 border border-zinc-200 bg-zinc-50 text-zinc-500 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="overline block mb-1.5">Telefoonnummer</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="06 12345678"
                className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044]"
                data-testid="profile-phone"
              />
            </div>
            <div>
              <label className="overline block mb-1.5 inline-flex items-center gap-1.5"><Globe size={11} /> {t("language")}</label>
              <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}
                className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044]"
                data-testid="profile-language"
              >
                {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <button onClick={save} disabled={saving}
              className="mt-2 h-10 px-5 bg-[#5BC044] hover:bg-[#4FAA3A] disabled:opacity-60 text-white rounded-md text-sm font-semibold"
              data-testid="profile-save"
            >
              {saving ? "Opslaan…" : "Opslaan"}
            </button>
          </div>
        </div>
      </div>

      {isChef && (
        <div className="card-flat p-6" data-testid="chef-restaurants-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline mb-1">Mijn restaurants</div>
              <h3 className="text-xl font-bold tracking-tight text-[#1B2D5C]">Werk je op meerdere locaties?</h3>
              <p className="text-sm text-zinc-500 mt-1">Voer een deelcode in om aan te sluiten bij een extra restaurant.</p>
            </div>
          </div>

          <div className="space-y-2 mb-5">
            {myRestaurants.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4 text-center border border-dashed border-zinc-200 rounded-md">Geen gekoppelde restaurants.</div>
            ) : myRestaurants.map((r) => (
              <div key={r.id} className="flex items-center justify-between py-2 px-3 border border-zinc-200 rounded-md bg-zinc-50/50" data-testid={`my-restaurant-${r.id}`}>
                <div className="flex items-center gap-2">
                  <MapPin size={14} className="text-[#5BC044]" />
                  <span className="font-medium text-sm">{r.name}</span>
                  <span className="text-xs text-zinc-500">{r.location}</span>
                </div>
                {myRestaurantIds.length > 1 && (
                  <button onClick={() => removeRestaurant(r.id)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`unlink-${r.id}`}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="overline block mb-1.5">Nieuwe deelcode</label>
              <input value={code} onChange={(e) => lookup(e.target.value)} placeholder="Bv. AB7CDE" maxLength={8}
                className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm uppercase tracking-[0.2em] font-mono focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044]"
                data-testid="add-code-input"
              />
            </div>
            <button onClick={addRestaurant} disabled={!preview || preview === "invalid"}
              className="h-10 px-4 bg-[#1B2D5C] hover:bg-[#142244] disabled:opacity-50 text-white rounded-md text-sm font-semibold inline-flex items-center gap-2"
              data-testid="add-code-button"
            >
              <Plus size={15} /> Toevoegen
            </button>
          </div>
          {preview && preview !== "invalid" && (
            <div className="mt-2 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="add-code-preview">
              <Check size={14} /> <b>{preview.name}</b> · {preview.location}
            </div>
          )}
          {preview === "invalid" && code.length >= 4 && (
            <div className="mt-2 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              <X size={14} /> Ongeldige deelcode
            </div>
          )}
        </div>
      )}

      {/* Security / 2FA */}
      <TwoFASection user={user} refreshUser={refreshUser} />
    </div>
  );
}

function TwoFASection({ user, refreshUser }) {
  const [setup, setSetup] = useState(null); // { qr_data_url, secret }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [disableCode, setDisableCode] = useState("");

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/2fa/setup");
      setSetup(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Mislukt"); }
    finally { setBusy(false); }
  };

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/2fa/enable", { code });
      toast.success("2FA ingeschakeld");
      setSetup(null); setCode("");
      await refreshUser?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
    finally { setBusy(false); }
  };

  const disable = async (e) => {
    e.preventDefault();
    if (!confirm("Weet je zeker dat je 2FA wil uitschakelen?")) return;
    setBusy(true);
    try {
      await api.post("/2fa/disable", { code: disableCode });
      toast.success("2FA uitgeschakeld");
      setDisableCode("");
      await refreshUser?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-flat p-5 mt-6" data-testid="twofa-section">
      <div className="overline mb-2">Beveiliging</div>
      <h3 className="text-lg font-bold tracking-tight text-[#1B2D5C] mb-1">Tweestapsverificatie (2FA)</h3>
      <p className="text-sm text-zinc-500 mb-4">Extra beveiliging via Google Authenticator, 1Password of Authy. Sterk aanbevolen voor admin-accounts.</p>

      {user?.twofa_enabled ? (
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded text-sm font-semibold mb-4">
            <Check size={14} /> 2FA is actief op dit account
          </div>
          <form onSubmit={disable} className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="overline block mb-1.5">Voer huidige code in om uit te schakelen</label>
              <input required maxLength={6} value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))} className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm font-mono tracking-[0.3em] text-center" placeholder="000000" data-testid="twofa-disable-code" />
            </div>
            <button type="submit" disabled={busy || disableCode.length !== 6} className="h-10 px-4 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-md text-sm font-semibold" data-testid="twofa-disable-button">
              Uitschakelen
            </button>
          </form>
        </div>
      ) : setup ? (
        <form onSubmit={enable} className="space-y-3" data-testid="twofa-setup-form">
          <p className="text-sm text-zinc-700">1. Scan de QR-code met je authenticator app:</p>
          <div className="flex items-start gap-4 flex-wrap">
            <img src={setup.qr_data_url} alt="2FA QR" className="w-40 h-40 border border-zinc-200 rounded" data-testid="twofa-qr-image" />
            <div className="text-xs text-zinc-500 max-w-[280px]">
              <p>Of voer deze sleutel handmatig in:</p>
              <code className="block mt-1 p-2 bg-zinc-100 rounded font-mono break-all select-all">{setup.secret}</code>
            </div>
          </div>
          <p className="text-sm text-zinc-700">2. Voer de 6-cijferige code uit je app in:</p>
          <input autoFocus required maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="w-full max-w-[200px] h-11 px-3 border border-zinc-300 rounded-md text-sm font-mono tracking-[0.4em] text-center"
            placeholder="000000"
            data-testid="twofa-enable-code" />
          <div className="flex gap-2">
            <button type="button" onClick={() => { setSetup(null); setCode(""); }} className="h-10 px-4 border border-zinc-300 rounded-md text-sm">Annuleren</button>
            <button type="submit" disabled={busy || code.length !== 6} className="h-10 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] disabled:opacity-50 text-white rounded-md text-sm font-semibold" data-testid="twofa-enable-button">
              Activeer 2FA
            </button>
          </div>
        </form>
      ) : (
        <button onClick={start} disabled={busy} className="inline-flex items-center gap-2 h-10 px-4 bg-[#1B2D5C] hover:bg-[#142244] text-white rounded-md text-sm font-semibold" data-testid="twofa-start-button">
          {busy ? "Bezig…" : "2FA inschakelen"}
        </button>
      )}
    </div>
  );
}
